import React, { useState, MouseEvent, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  IconButton,
  List,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Typography,
  Checkbox,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Button, Dots, TextField } from '~/UIKit';
import { useStyles } from '../UrbanSportsConnect/ConnectUrbanSports.styles';
import { ChannelCategory } from '~/Models/DTO/IntegrationChannels.model';
import { useToastMessage, ToastVariant } from '~/Hooks';
import { Error, SearchTextField } from '~/Components';
import { useFetchClasses } from '~/requests/adapterSvc/useFetchChannelCategories';
import { useRequestChannelCategories } from '~/requests/adapterSvc/useRequestChannelCategories';
import { useRequestUpdateChannelCategories } from '~/requests/adapterSvc/useRequestUpdateChannelCategories';
import { useRequestUscCategories } from '~/requests/eventService/useRequestUscCategories';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ListItemSelect from '~/Screens/Events/EventPage/Components/ListItemSelect';
import { debounce } from 'lodash-es';
import { useSportCatgeroy } from '~/Helpers/SportCategory';

interface MappingCategoriesProps {
  id: string;
  showFetchClassesBtn?: boolean;
}

interface selectedUscCategory {
  name: string;
  index: number;
}

export const MappingCategories = ({ id, showFetchClassesBtn = false }: MappingCategoriesProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { showToast } = useToastMessage();
  const { getCategory } = useSportCatgeroy();
  const getName = (type: any) => getCategory(type);

  const {
    data: categories,
    error: fetchCategoriesError,
    isLoading: fetchCategoriesLoading,
    refetch: fetchCategories,
  } = useRequestChannelCategories({ channelId: id });

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [cardDisabled, setCardDisabled] = useState<boolean>(true);
  const [selectedLocationName, setSelectedLocationName] = useState<selectedUscCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number>(-1);
  const [mappedCategories, setMappedCategories] = useState<ChannelCategory[]>([]);

  const [isNew, setIsNew] = useState(id);
  const [showFetchClasses, setShowFetchClasses] = useState(showFetchClassesBtn);
  const [fetchClassesDone, setFetchClassesDone] = useState(false);
  const { error: USCCategoriesError, data: USCCategories } = useRequestUscCategories();

  const [searchQuery, setSearchQuery] = useState('');
  const options = [t('app.internal.change'), t('app.internal.ignore'), t('app.internal.unmap')];

  const {
    isLoading: updateCategoriesLoading,
    isSuccess: categoriesUpdated,
    mutate: updateCategories,
  } = useRequestUpdateChannelCategories({ channelId: id });

  const { refetch: fetchClasses, isLoading: fetchClassesLoading } = useFetchClasses({
    onSuccess: () => setFetchClassesDone(true),
  });

  const filteredCategories =
    USCCategories?.filter((category) =>
      category.name.toLowerCase().includes(searchQuery.toLowerCase()),
    ) || [];

  const handleClosePopover = () => {
    setPopoverOpen(false);
    setPopoverAnchorEl(null);
    setSearchQuery('');
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
    setMenuAnchorEl(null);
  };

  const handleOpenPopover = (e: MouseEvent<HTMLElement>, index: number) => {
    setPopoverAnchorEl(e.currentTarget);
    setPopoverOpen(true);
    setMenuOpen(false);
    setSelectedCategory(index);
  };

  const handleOpenMenu = (e: MouseEvent<HTMLElement>, index: number) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuOpen(true);
    setPopoverOpen(false);
    setSelectedCategory(index);
  };

  const handleSelectItem = (categoryId: string | number, categoryName: string) => {
    setSelectedLocationName((previousData) => {
      const previousCategories = [...previousData];
      previousCategories[selectedCategory] = {
        ...previousCategories[selectedCategory],
        name: categoryName,
        index: categoryId as number,
      };
      return previousCategories;
    });
    setMappedCategories((prevCategories) => {
      if (!prevCategories) return prevCategories;
      const updatedCategories = [...prevCategories];
      updatedCategories[selectedCategory] = {
        ...updatedCategories[selectedCategory],
        value: String(categoryId),
        ignored: false,
      };
      return updatedCategories;
    });
    handleClosePopover();
  };

  const handleSetSeatLimits = (value: number, category: number) => {
    setMappedCategories((prevCategories) => {
      const updatedCategories = [...prevCategories];
      updatedCategories[category] = {
        ...updatedCategories[category],
        seats_limit: `${value}`,
      };
      return updatedCategories;
    });
  };

  const handleMenuOptionClick = (option: string) => {
    switch (option) {
      case t('app.internal.unmap'):
        setMappedCategories((prevCategories) => {
          const updatedCategories = [...prevCategories];
          updatedCategories[selectedCategory] = {
            ...updatedCategories[selectedCategory],
            value: '',
            ignored: false,
          };
          return updatedCategories;
        });
        setSelectedLocationName((previousData) => {
          const previousCategories = [...previousData];
          previousCategories[selectedCategory] = { name: '', index: 0 };
          return previousCategories;
        });
        break;
      case t('app.internal.change'):
        if (!menuAnchorEl) return;
        handleOpenPopover(
          { currentTarget: menuAnchorEl } as MouseEvent<HTMLElement>,
          selectedCategory,
        );
        break;
      case t('app.internal.ignore'):
        setMappedCategories((prevCategories) => {
          const updatedCategories = [...prevCategories];
          updatedCategories[selectedCategory] = {
            ...updatedCategories[selectedCategory],
            ignored: true,
          };
          return updatedCategories;
        });
        break;
      default:
        break;
    }
    handleCloseMenu();
  };

  const handleUpdateCategories = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const updatedCategories = mappedCategories.map(({ name, ...rest }) => rest);
    updateCategories({ categories: updatedCategories });
  };

  const handleFetchCategories = () => {
    fetchCategories();
  };

  const handleFetchClasses = () => {
    fetchClasses(id);
  };

  useEffect(() => {
    if (id !== isNew && id !== 'new') {
      setShowFetchClasses(true);
    } else {
      setIsNew(id);
    }
  }, [id]);

  useEffect(() => {
    if (categories?.data && USCCategories) {
      const initialMappedCategories = categories.data.map((category) => {
        const matchingUscCategory = USCCategories.find(
          (uscCategory) => String(uscCategory.id) === category.value,
        );
        return {
          ...category,
          name: matchingUscCategory ? matchingUscCategory.name : '',
          seats_limit: `${category.seats_limit || 0}`,
        };
      });
      setMappedCategories(initialMappedCategories);
    }
    if (id === 'new') {
      setCardDisabled(true);
    } else {
      setCardDisabled(false);
    }
    if (categoriesUpdated) {
      showToast('CONNECTED', t('app.integration.updated'), ToastVariant.SUCCESS, true);
      setTimeout(() => window.location.reload(), 1000);
    }
  }, [categories?.data?.length, id, categoriesUpdated, USCCategories?.length]);

  const disableFetchCategoryButton =
    cardDisabled ||
    fetchCategoriesLoading ||
    fetchClassesLoading ||
    (showFetchClasses && !fetchClassesDone);

  const disableFetchClassesButton = cardDisabled || fetchClassesLoading;

  const debouncedSearch = useRef(
    debounce((query: string) => {
      setSearchQuery(query);
    }, 500),
  ).current;

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  return (
    <Card
      className={
        cardDisabled
          ? `${classes.itemDisabled} ${classes.magiclineCard} ${classes.card}`
          : `${classes.magiclineCard} ${classes.card}`
      }
    >
      <Error error={fetchCategoriesError} />
      <Error error={USCCategoriesError} />
      <CardContent className={classes.magiclineCardContent}>
        <div className={`${classes.titleSection}`}>
          <Typography
            variant="h5"
            className={
              cardDisabled
                ? `${classes.sectionDisabled} ${classes.heading} ${classes.magiclineInnerPadding}`
                : `${classes.heading} ${classes.magiclineInnerPadding}`
            }
          >
            {t('app.title.mapping.categories')}
          </Typography>
        </div>
        <div className={`${classes.descriptionSection} ${classes.magiclineInnerPadding}`}>
          <Typography
            className={
              cardDisabled
                ? `${classes.sectionDisabled} ${classes.magiclineDescription}`
                : classes.magiclineDescription
            }
            variant="body2"
          >
            {t('mapping.categories.description')}
          </Typography>
          {fetchCategoriesLoading ? (
            <Dots />
          ) : categories?.data?.length === 0 || categories?.data === undefined ? (
            <div className={classes.categoryBtnSection}>
              {showFetchClasses && (
                <Button
                  onClick={handleFetchClasses}
                  variant="contained"
                  size="medium"
                  color="primary"
                  disabled={disableFetchClassesButton}
                  className={classes.button}
                >
                  {t('button.fetch.classes')}
                </Button>
              )}
              <Button
                onClick={handleFetchCategories}
                variant="contained"
                size="medium"
                color="primary"
                disabled={disableFetchCategoryButton}
                className={classes.button}
              >
                {t('button.fetch.categories')}
              </Button>
            </div>
          ) : (
            <Button
              onClick={handleUpdateCategories}
              variant="contained"
              size="medium"
              color="primary"
              disabled={updateCategoriesLoading || cardDisabled}
              className={classes.button}
            >
              {t('app.save')}
            </Button>
          )}
        </div>
        {categories?.data?.length !== 0 &&
          categories?.data?.map((category, index) => (
            <div className={classes.categoryWrapper} key={`${category?.name}-${index}`}>
              <div
                key={category.key}
                className={`${classes.inputMagicline} ${classes.categoryCard}`}
              >
                <Typography variant="body2" className={classes.selectLabel}>
                  {category.name}
                </Typography>
                <div className={classes.selectedItems}>
                  {fetchCategoriesLoading ? (
                    <Dots />
                  ) : (
                    <>
                      <Typography className={classes.selectedItem}>
                        {selectedLocationName &&
                        selectedLocationName[index] &&
                        selectedLocationName[index].index ? (
                          getName(selectedLocationName[index].index)
                        ) : mappedCategories &&
                          mappedCategories[index] &&
                          mappedCategories[index].value ? (
                          getName(mappedCategories[index].value)
                        ) : (
                          <span className={classes.sectionDisabled}>
                            {t('magicline.categories.sport')}
                          </span>
                        )}
                        {mappedCategories &&
                          mappedCategories[index] &&
                          mappedCategories[index].ignored && (
                            <span className={classes.listItemTitleSpan}>
                              ({t('app.internal.ignored')})
                            </span>
                          )}
                      </Typography>
                      <IconButton
                        className={classes.iconButton}
                        onClick={(e) => handleOpenMenu(e, index)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                      {selectedCategory === index && (
                        <Menu
                          className={classes.optionsMenu}
                          anchorEl={menuAnchorEl}
                          open={menuOpen}
                          onClose={handleCloseMenu}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                          }}
                        >
                          {options.map((option) => (
                            <MenuItem key={option} onClick={() => handleMenuOptionClick(option)}>
                              {option}
                            </MenuItem>
                          ))}
                        </Menu>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className={classes.inportSpot}>
                <TextField
                  newDesign
                  type="number"
                  label={t('form.integration.field.spots')}
                  defaultValue={category?.seats_limit || 0}
                  value={mappedCategories[index]?.seats_limit || 0}
                  InputProps={{ inputProps: { min: 0 } }}
                  onChange={(e) => handleSetSeatLimits(Number(e.target.value), index)}
                />
              </div>
            </div>
          ))}
      </CardContent>
      <Popover
        open={popoverOpen}
        anchorEl={popoverAnchorEl}
        onClose={handleClosePopover}
        classes={{ paper: `${classes.popoverPaper}` }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          style: {
            marginLeft: '9px',
            marginTop: '8px',
          },
        }}
      >
        <div>
          <SearchTextField
            id="searchCategories"
            onChange={(e) => debouncedSearch(e.target.value)}
            placeholder={t('app.search.categories')}
            withBackground
            autoFocus={popoverOpen}
          />
        </div>
        <List data-testid="ChangeCategoryList">
          {filteredCategories.map((category) => {
            const isChecked = mappedCategories[selectedCategory]?.value === String(category.id);
            return (
              <ListItemSelect
                key={category.id}
                onClick={() => handleSelectItem(category.id, category.name)}
                name={getName(category.id)}
                testId="ChangeCategoryListItem"
                style={{ justifyContent: 'space-between' }}
              >
                <div style={{ flex: 1 }}>
                  <ListItemText style={{ textAlign: 'left' }} />
                </div>
                <Checkbox
                  edge="end"
                  checked={isChecked}
                  onChange={() =>
                    handleSelectItem(isChecked ? '' : category.id, isChecked ? '' : category.name)
                  }
                  color="primary"
                  style={{
                    marginLeft: 'auto',
                    paddingRight: '16px',
                  }}
                />
              </ListItemSelect>
            );
          })}
        </List>
      </Popover>
    </Card>
  );
};
